import { useEffect, useState } from "react";
import api, { apiErrorMessage, formatDate, safeLink } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { PackagePlus, X, Trash, Coins } from "lucide-react";

const STATUS = ["nowe", "rozpatrywane", "zaakceptowane", "odrzucone"];
const STATUS_LABEL = { nowe: "Nowe", rozpatrywane: "Rozpatrywane", zaakceptowane: "Zaakceptowane", odrzucone: "Odrzucone" };

export default function AdminCustomRequests() {
  const [list, setList] = useState([]);
  const [comments, setComments] = useState({});
  const [prices, setPrices] = useState({});
  const [addingFor, setAddingFor] = useState(null); // request object being promoted to catalog
  const [catalogForm, setCatalogForm] = useState({ sku: "", category: "", unit: "szt.", price_net: "", vat: 23 });

  const reload = () => api.get("/custom-product-requests").then((r) => {
    setList(r.data);
    const p = {};
    r.data.forEach((req) => { if (req.estimated_price_net != null) p[req.id] = req.estimated_price_net; });
    setPrices(p);
  });
  useEffect(() => { reload(); }, []);

  const review = async (id, status) => {
    try {
      const payload = { status, office_land_comment: comments[id] || null };
      if (prices[id] !== "" && prices[id] != null) payload.estimated_price_net = Number(prices[id]);
      await api.patch(`/custom-product-requests/${id}`, payload);
      toast.success("Status zaktualizowany");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const savePrice = async (id) => {
    try {
      const cur = list.find((r) => r.id === id);
      await api.patch(`/custom-product-requests/${id}`, {
        status: cur?.status || "rozpatrywane",
        estimated_price_net: Number(prices[id] || 0),
      });
      toast.success("Cena zapisana");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const deleteReq = async (id) => {
    if (!window.confirm("Usunąć zapytanie? Tej operacji nie da się cofnąć.")) return;
    try {
      await api.delete(`/custom-product-requests/${id}`);
      toast.success("Usunięto zapytanie");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const startAddToCatalog = (req) => {
    setAddingFor(req);
    // Suggest SKU based on product name
    const slug = req.product_name.replace(/[^A-Za-z0-9]/g, "").slice(0, 6).toUpperCase();
    setCatalogForm({ sku: `NEW-${slug}`, category: "Inne", unit: "szt.", price_net: "", vat: 23 });
  };

  const submitAddToCatalog = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/custom-product-requests/${addingFor.id}/add-to-catalog`, {
        sku: catalogForm.sku.trim(),
        category: catalogForm.category.trim(),
        unit: catalogForm.unit.trim(),
        price_net: Number(catalogForm.price_net),
        vat: Number(catalogForm.vat),
        auto_approve_request: true,
      });
      toast.success(`Dodano "${addingFor.product_name}" do katalogu`);
      setAddingFor(null);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader testId="admin-custom" title="Produkty spoza katalogu" subtitle="Zapytania od pracowników NDI" />

      <div className="ol-card overflow-hidden">
        {list.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Brak zapytań.</div>
        ) : (
          <table className="ol-table" data-testid="admin-custom-table">
            <thead>
              <tr><th>Data</th><th>Pracownik</th><th>Produkt</th><th>Ilość</th><th>Status</th><th>Cena Office-Land</th><th>Komentarz kierownika</th><th>Akcje</th></tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const added = !!r.added_to_catalog_product_id;
                return (
                  <tr key={r.id} className="align-top">
                    <td className="text-xs text-slate-600">{formatDate(r.created_at)}</td>
                    <td>{r.requested_by_name}</td>
                    <td>
                      <p className="font-medium">{r.product_name}</p>
                      {r.product_link && <a href={safeLink(r.product_link)} target="_blank" rel="noreferrer" className="ol-link text-xs">link</a>}
                      {r.comment && <p className="text-xs text-slate-600 mt-1">{r.comment}</p>}
                      {r.suggest_add_to_catalog && (
                        <p className="text-xs text-[#CC4B00] font-semibold mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF4ED] border border-[#FF6B1A]/30">
                          ✦ Sugerowane do katalogu
                        </p>
                      )}
                      {added && <p className="text-xs text-emerald-700 mt-1 font-semibold">✓ W katalogu</p>}
                    </td>
                    <td>{r.estimated_quantity}</td>
                    <td><span className="ol-badge bg-slate-100 text-slate-700 border border-slate-200">{STATUS_LABEL[r.status]}</span></td>
                    <td className="text-sm">
                      {r.estimated_price_net != null
                        ? <span className="font-semibold text-slate-900">{Number(r.estimated_price_net).toFixed(2)} zł</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="text-sm text-slate-600 max-w-xs">{r.manager_comment || "—"}</td>
                    <td>
                      <div className="flex flex-col gap-1.5 w-56">
                        {!added && (
                          <button onClick={() => startAddToCatalog(r)} data-testid={`add-to-catalog-${r.id}`}
                            className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold">
                            <PackagePlus className="w-3.5 h-3.5" /> Dodaj do katalogu na stałe
                          </button>
                        )}
                        <div className="flex gap-1">
                          <div className="relative flex-1">
                            <Coins className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="number" step="0.01" min="0"
                              value={prices[r.id] ?? ""}
                              onChange={(e) => setPrices({ ...prices, [r.id]: e.target.value })}
                              placeholder="Cena netto"
                              data-testid={`price-${r.id}`}
                              className="w-full pl-6 pr-1 py-1 rounded border border-slate-300 text-xs focus:border-[#FF6B1A] focus:outline-none"
                            />
                          </div>
                          <button onClick={() => savePrice(r.id)} data-testid={`save-price-${r.id}`}
                            className="px-2 py-1 rounded border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold">
                            Zapisz
                          </button>
                        </div>
                        <input placeholder="Komentarz Office-Land" value={comments[r.id] || ""}
                          onChange={(e) => setComments({ ...comments, [r.id]: e.target.value })}
                          className="px-2 py-1 rounded border border-slate-300 text-xs focus:border-[#FF6B1A] focus:outline-none" />
                        <div className="flex gap-1 flex-wrap">
                          {STATUS.filter(s => s !== r.status).map((s) => (
                            <button key={s} onClick={() => review(r.id, s)} data-testid={`admin-set-${r.id}-${s}`}
                              className="px-2 py-1 rounded border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold">
                              {STATUS_LABEL[s]}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => deleteReq(r.id)} data-testid={`delete-custom-${r.id}`}
                          className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold">
                          <Trash className="w-3 h-3" /> Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addingFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="add-to-catalog-modal">
          <form onSubmit={submitAddToCatalog} className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <PackagePlus className="w-5 h-5 text-[#FF6B1A]" />
                <h3 className="font-bold text-slate-900">Dodaj do katalogu na stałe</h3>
              </div>
              <button type="button" onClick={() => setAddingFor(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <p className="text-slate-500 text-xs uppercase tracking-wide font-semibold mb-1">Produkt z zapytania</p>
                <p className="font-semibold text-slate-900">{addingFor.product_name}</p>
                {addingFor.product_link && <a href={safeLink(addingFor.product_link)} target="_blank" rel="noreferrer" className="ol-link text-xs">{addingFor.product_link}</a>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">SKU *</label>
                  <input required value={catalogForm.sku} onChange={(e) => setCatalogForm({ ...catalogForm, sku: e.target.value })} data-testid="cat-sku"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Kategoria *</label>
                  <input required value={catalogForm.category} onChange={(e) => setCatalogForm({ ...catalogForm, category: e.target.value })} data-testid="cat-category"
                    placeholder="Biuro / BHP / Środki czystości / Narzędzia / Inne"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Jednostka *</label>
                  <input required value={catalogForm.unit} onChange={(e) => setCatalogForm({ ...catalogForm, unit: e.target.value })}
                    placeholder="szt. / para / opak. / ryza"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Cena netto (PLN) *</label>
                  <input required type="number" step="0.01" min="0" value={catalogForm.price_net} onChange={(e) => setCatalogForm({ ...catalogForm, price_net: e.target.value })} data-testid="cat-price"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                </div>
              </div>
              <p className="text-xs text-slate-500">💡 Po dodaniu, produkt natychmiast pojawi się w katalogu wszystkich pracowników, a zapytanie zostanie automatycznie oznaczone jako zaakceptowane.</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button type="button" onClick={() => setAddingFor(null)} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Anuluj</button>
              <button type="submit" data-testid="confirm-add-to-catalog"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
                <PackagePlus className="w-4 h-4" /> Dodaj do katalogu
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
